// AgentDeleteModal.tsx — "Delete this agent?" confirmation (admin-only page).

import React from 'react';
import { ArrowLeftRight, Trash2, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { C } from '../../../components/GlobalComponents/uiTokens';
import ProposalDialog, { Notice } from '../../Proposals/parts/ProposalDialog';

interface AgentDeleteModalProps {
  visible: boolean;
  name?: string;
  /** the agent came from a lead whose history is kept: deleting would throw it away */
  hasLeadHistory?: boolean;
  /** offered when there is history: keep it by moving the agent back to Leads instead */
  onMoveInstead?: () => void;
  loading?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const AgentDeleteModal: React.FC<AgentDeleteModalProps> = ({ visible, name, hasLeadHistory = false, onMoveInstead, loading = false, error, onCancel, onConfirm }) => (
  <ProposalDialog
    visible={visible}
    onHide={onCancel}
    title="Delete this agent?"
    subtitle={name ? <strong style={{ color: C.teal }}>{name}</strong> : 'This cannot be undone'}
    icon={Trash2}
    tone="danger"
    busy={loading}
    maxWidth="440px"
    footer={
      <>
        <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={onCancel} disabled={loading} />
        {hasLeadHistory && onMoveInstead && <GlobalButton label="MOVE TO LEADS INSTEAD" variant="outline" icon={ArrowLeftRight} onClick={onMoveInstead} disabled={loading} />}
        <GlobalButton label={loading ? 'DELETING…' : hasLeadHistory ? 'DELETE WITH HISTORY' : 'DELETE'} variant="danger" icon={Trash2} onClick={onConfirm} loading={loading} />
      </>
    }
  >
    {error && <Notice tone="error">{error}</Notice>}
    {hasLeadHistory ? (
      <p style={{ margin: 0, fontSize: '0.78rem', color: C.sub, lineHeight: 1.6 }}>
        This agent used to be a lead, and that lead’s <strong>full history is still kept</strong> — status, notes, call log and proposals. Deleting now removes the agent <strong>and all of that for good</strong>. To keep it, move them back to Leads instead.
      </p>
    ) : (
      <p style={{ margin: 0, fontSize: '0.78rem', color: C.sub, lineHeight: 1.6 }}>
        The agent’s record is removed for good. If you only want to stop using them for now, mark them inactive instead — you keep their details.
      </p>
    )}
  </ProposalDialog>
);

export default AgentDeleteModal;